/**
 * Firebase app, Auth, and Messaging initialization.
 */
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
  RecaptchaVerifier,
  type Persistence,
} from 'firebase/auth';
import { getMessaging, type Messaging } from 'firebase/messaging';

/** Vite-injected Firebase project keys. */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/** Shared Firebase app instance. */
const app = initializeApp(firebaseConfig);
export { app };
/** Shared Firebase Auth instance (French locale, local persistence by default). */
export const auth = getAuth(app);

/** FCM instance, or `null` when Messaging is unsupported (e.g. Firefox private browsing). */
const messaging: Messaging | null = (() => {
  try {
    return getMessaging(app);
  } catch (error) {
    console.warn('Firebase Messaging not supported in this environment:', error);
    return null;
  }
})();
export { messaging };

auth.languageCode = 'fr';

/** Use local persistence when "remember me" is on, otherwise session persistence. */
export const setAuthPersistence = async (rememberMe: boolean): Promise<void> => {
  const persistence: Persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;

  await setPersistence(auth, persistence);
};

/** Invisible reCAPTCHA for phone authentication. */
export const initRecaptchaVerifier = (containerId = 'recaptcha-container'): RecaptchaVerifier => {
  const existingContainer = document.getElementById(containerId);
  if (existingContainer) {
    existingContainer.innerHTML = '';
  }

  return new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => {
      console.warn('reCAPTCHA expired');
    },
  });
};

/** Calls `verifier.clear()` so a new invisible widget can be created (phone auth). */
export const cleanupRecaptcha = (verifier: RecaptchaVerifier | null) => {
  if (verifier) {
    try {
      verifier.clear();
    } catch (error) {
      console.error('Error clearing reCAPTCHA:', error);
    }
  }
};
